import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { Panel } from "./chartTheme";
import { SERVERS, resolveServers, probe } from "../../lib/servers";
import { toast } from "sonner";
import { api, fmt } from "../../lib/api";
import { useSort, SortTh } from "../../lib/useSort";

const H_COLS = [["rank", "Priority"], ["name", "Server"], ["uptime", "Uptime"], ["ok", "Successful tests"], ["fails", "Failed attempts"], ["ping", "Avg ping"], ["dl", "Avg ↓"]];
const H_ACC = { ...Object.fromEntries(H_COLS.map(([k]) => [k, (r) => r[k]])), rank: (r) => (r.rank < 0 ? 99 : r.rank) };

const WEEK = 7 * 864e5;
const upTone = (u) => (u == null ? "text-neutral-500" : u >= 99 ? "text-[#32D74B]" : u >= 90 ? "text-[#FF9F0A]" : "text-[#FF3B30]");

const health = (tests, id) => {
  const ok = tests.filter((t) => (t.test_server || "app") === id);
  const fails = tests.filter((t) => t.failed_servers?.includes(id)).length;
  const attempts = ok.length + fails;
  return {
    ok: ok.length, fails, attempts,
    uptime: attempts ? (ok.length / attempts) * 100 : null,
    ping: ok.length ? ok.reduce((s, t) => s + t.ping_ms, 0) / ok.length : null,
    dl: ok.length ? ok.reduce((s, t) => s + t.download_mbps, 0) / ok.length : null,
  };
};

export const ServerHealth = ({ tests }) => {
  const [cfg, setCfg] = useState(null);
  const [live, setLive] = useState({});
  useEffect(() => { api.settings().then(setCfg).catch(() => setCfg({})); }, []);
  const week = tests.filter((t) => Date.now() - new Date(t.created_at) <= WEEK);
  const prio = cfg?.server_priority || [];
  const rows = SERVERS.filter((s) => s.id !== "custom" || cfg?.custom_server_url)
    .map((s) => ({ ...s, name: s.id === "custom" ? cfg.custom_server_name || cfg.custom_server_url : s.name, rank: prio.indexOf(s.id), ...health(week, s.id) }))
    .sort((a, b) => (a.rank < 0) - (b.rank < 0) || a.rank - b.rank || b.attempts - a.attempts);

  const makePrimary = async (id) => {
    const server_priority = [id, ...prio.filter((p) => p !== id)].slice(0, 3);
    try {
      setCfg(await api.saveSettings({ ...cfg, server_priority }));
      toast.success("Server priority updated");
    } catch { toast.error("Could not update priority"); }
  };

  const { sorted, sort, toggle } = useSort(rows, H_ACC);

  const checkAll = async () => {
    const targets = Object.fromEntries(resolveServers({ ...cfg, server_priority: rows.map((r) => r.id) }).map((s) => [s.id, s.target]));
    for (const r of rows) {
      setLive((l) => ({ ...l, [r.id]: "…" }));
      try { const ms = await probe(targets[r.id]); setLive((l) => ({ ...l, [r.id]: `${ms} ms` })); }
      catch { setLive((l) => ({ ...l, [r.id]: "down" })); }
    }
  };

  return (
    <Panel title="Server health board: last 7 days" className="lg:col-span-3" testId="report-server-health">
      <div className="flex justify-end -mt-8 mb-2 no-print">
        <button data-testid="server-health-check-button" onClick={checkAll} disabled={!cfg} className="btn-ghost text-xs flex items-center gap-1"><Activity className="w-3 h-3" /> Check now</button>
      </div>
      <table className="w-full text-xs" data-testid="server-health-table">
        <thead className="text-neutral-500 text-left">
          <tr>
            {H_COLS.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={sort} toggle={toggle} />)}
            <th className="py-1.5 pr-3 font-normal uppercase tracking-wider">Live check</th><th />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.id} className="border-t border-white/5" data-testid={`server-health-row-${r.id}`}>
              <td className="py-2 pr-3">{r.rank >= 0 ? <span className="bg-[#007AFF] text-white px-2 py-0.5">{["Primary", "Failover 1", "Failover 2"][r.rank]}</span> : <span className="text-neutral-600">Not used</span>}</td>
              <td className="py-2 pr-3 text-white">{r.name}</td>
              <td className={`py-2 pr-3 font-medium ${upTone(r.uptime)}`} data-testid={`server-health-uptime-${r.id}`}>{r.uptime == null ? "No attempts" : `${fmt(r.uptime)}%`}</td>
              <td className="py-2 pr-3">{r.ok}</td>
              <td className={`py-2 pr-3 ${r.fails ? "text-[#FF3B30]" : ""}`}>{r.fails}</td>
              <td className="py-2 pr-3">{r.ping == null ? "—" : `${fmt(r.ping, 0)} ms`}</td>
              <td className="py-2 pr-3">{r.dl == null ? "—" : `${fmt(r.dl)} Mbps`}</td>
              <td className={`py-2 pr-3 ${live[r.id] === "down" ? "text-[#FF3B30]" : "text-[#32D74B]"}`} data-testid={`server-health-live-${r.id}`}>{live[r.id] || "—"}</td>
              <td className="py-2 text-right no-print">
                {r.rank !== 0 && (
                  <button data-testid={`make-primary-${r.id}`} onClick={() => makePrimary(r.id)} className="btn-ghost text-[11px] px-2 py-1 hover:!border-[#007AFF] hover:!text-white">Make primary</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-neutral-600 mt-3">
        Uptime = successful tests ÷ (successful + failed attempts) from reps' phones. Failover servers are only tried when a higher-priority server fails, so they may show few attempts.
        Live check pings each server from this browser now. "Make primary" moves a server to the top and shifts the others down (max 3).
      </div>
    </Panel>
  );
};
